import { useState } from 'react';
import { ReactComponent as DbtCloudIcon } from '@elementary/common-ui/assets/orchestrators/dbt-cloud-icon.svg';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import {
  FlexColumn,
  Flex,
  Button,
  LoadingButton,
  PasswordField,
  TextField,
  Loading,
} from '@elementary/common-ui/components';
import { AI_MCP_DBT_CLOUD } from '@elementary/common-ui/constants/queryKeys';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import {
  DbtCloudConnection,
  fetchDbtCloudConnections,
} from '@elementary/common-ui/services/api';
import { Typography, InputLabel } from '@mui/material';
import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as settingsApi from 'shared/services/settingsApi';
import { fontWeight } from '@elementary/common-ui/style/utils';
import {
  Form,
  Frame,
  FieldsContainer,
  Description,
} from './settingsCardStyles';

type DbtCloudForm = {
  name: string;
  hostUrl: string;
  serviceToken: string;
  dbtCloudProdEnvironmentId: string;
};

// Surface the server's validation detail (e.g. the dbt Cloud host allowlist
// 422) instead of a generic message. FastAPI returns `detail` as a string for
// HTTPExceptions and as a list of `{ msg }` for pydantic validation errors.
type ApiError = AxiosError<{ detail?: string | { msg: string }[] }>;

const getSaveErrorMessage = (error: ApiError): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return 'Failed to save dbt Cloud connection.';
};

type ConnectionFormProps = {
  connection?: DbtCloudConnection;
  canEdit: boolean;
  onDone: () => void;
};

const ConnectionForm = ({
  connection,
  canEdit,
  onDone,
}: ConnectionFormProps) => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isEdit = connection !== undefined;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DbtCloudForm>({
    defaultValues: {
      name: connection?.name ?? '',
      hostUrl: connection?.hostUrl ?? '',
      serviceToken: '',
      dbtCloudProdEnvironmentId: connection?.dbtCloudProdEnvironmentId ?? '',
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [AI_MCP_DBT_CLOUD] });

  const { mutate: save, isLoading: isSaving } = useMutation(
    (form: DbtCloudForm) => {
      const prodEnv = form.dbtCloudProdEnvironmentId.trim() || null;
      if (isEdit) {
        const payload: settingsApi.UpdateDbtCloudConnectionApi = {
          name: form.name.trim(),
          hostUrl: form.hostUrl.trim(),
          dbtCloudProdEnvironmentId: prodEnv,
        };
        if (form.serviceToken.trim()) {
          payload.serviceToken = form.serviceToken.trim();
        }
        return settingsApi.updateDbtCloudConnection(connection.id, payload);
      }
      return settingsApi.createDbtCloudConnection({
        name: form.name.trim(),
        hostUrl: form.hostUrl.trim(),
        serviceToken: form.serviceToken.trim(),
        dbtCloudProdEnvironmentId: prodEnv,
      });
    },
    {
      onSuccess: () => {
        openSnackbar({ message: 'dbt Cloud connection saved.' });
        invalidate();
        onDone();
      },
      onError: (error: ApiError) =>
        openSnackbar({ message: getSaveErrorMessage(error), variant: 'error' }),
    },
  );

  return (
    <Form onSubmit={handleSubmit((form) => save(form))}>
      <FieldsContainer>
        <FlexColumn>
          <InputLabel shrink>Connection name</InputLabel>
          <TextField
            size="large"
            disabled={!canEdit}
            placeholder="e.g. Analytics prod"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', { required: 'Connection name is required' })}
          />
        </FlexColumn>
        <FlexColumn>
          <InputLabel shrink>MCP server URL</InputLabel>
          <TextField
            size="large"
            disabled={!canEdit}
            placeholder="https://cloud.getdbt.com/api/ai/v1/mcp/"
            error={!!errors.hostUrl}
            helperText={errors.hostUrl?.message}
            {...register('hostUrl', {
              required: 'MCP server URL is required',
            })}
          />
        </FlexColumn>
        <FlexColumn>
          <InputLabel shrink>Service token</InputLabel>
          <PasswordField
            size="large"
            disabled={!canEdit}
            placeholder={
              isEdit
                ? `Stored token ending in ••••${connection.tokenLast4 ?? ''}`
                : 'Enter your dbt Cloud service token'
            }
            error={!!errors.serviceToken}
            helperText={errors.serviceToken?.message}
            {...register('serviceToken', {
              required: !isEdit ? 'Service token is required' : false,
            })}
          />
        </FlexColumn>
        <FlexColumn>
          <InputLabel shrink>Production environment ID (optional)</InputLabel>
          <TextField
            size="large"
            disabled={!canEdit}
            placeholder="e.g. 123456"
            {...register('dbtCloudProdEnvironmentId')}
          />
        </FlexColumn>
        <Flex sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="text" color="secondary" onClick={onDone}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isSaving}
            variant="outlined"
            disabled={!canEdit}
          >
            Save
          </LoadingButton>
        </Flex>
      </FieldsContainer>
    </Form>
  );
};

type ConnectionRowProps = {
  connection: DbtCloudConnection;
  canEdit: boolean;
  onEdit: () => void;
};

const ConnectionRow = ({ connection, canEdit, onEdit }: ConnectionRowProps) => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { mutate: remove, isLoading: isRemoving } = useMutation(
    () => settingsApi.deleteDbtCloudConnection(connection.id),
    {
      onSuccess: () => {
        openSnackbar({ message: 'dbt Cloud connection removed.' });
        queryClient.invalidateQueries({ queryKey: [AI_MCP_DBT_CLOUD] });
      },
      onError: () =>
        openSnackbar({
          message: 'Failed to remove dbt Cloud connection.',
          variant: 'error',
        }),
    },
  );

  return (
    <Flex
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'border.translucent',
        paddingTop: 2,
      }}
    >
      <FlexColumn>
        <Typography sx={{ fontWeight: fontWeight('semibold') }}>
          {connection.name}
        </Typography>
        <Typography variant="body2" color="content.translucent.secondary">
          {connection.hostUrl}
        </Typography>
        {connection.tokenLast4 && (
          <Typography variant="body2" color="content.translucent.secondary">
            Token ending in ••••{connection.tokenLast4}
          </Typography>
        )}
      </FlexColumn>
      <Flex sx={{ gap: 1 }}>
        <Button variant="text" disabled={!canEdit} onClick={onEdit}>
          Edit
        </Button>
        <Button
          variant="text"
          color="secondary"
          disabled={!canEdit || isRemoving}
          onClick={() => remove()}
        >
          Remove
        </Button>
      </Flex>
    </Flex>
  );
};

const AiMcpSection = () => {
  const hasAccountPermission = useAccountPermission();
  const canEdit = hasAccountPermission('EDIT_INTEGRATIONS');
  // null = not editing; 'new' = add form; otherwise the connection id.
  const [editing, setEditing] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery(
    [AI_MCP_DBT_CLOUD],
    fetchDbtCloudConnections,
  );

  if (isLoading) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  if (isError) {
    return (
      <Frame>
        <Typography color="error">
          Couldn&apos;t load dbt Cloud connections.
        </Typography>
      </Frame>
    );
  }

  const connections = data?.connections ?? [];
  const editingConnection = connections.find((c) => c.id === editing);

  return (
    <Frame>
      <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <FlexColumn>
          <Flex sx={{ alignItems: 'center', gap: 1 }}>
            <DbtCloudIcon width={20} height={20} />
            <Typography sx={{ fontWeight: fontWeight('semibold') }}>
              dbt Cloud
            </Typography>
          </Flex>
          <Description>
            Connect one or more dbt Cloud accounts so AI agents can use their
            MCP tools. Paste the dbt Cloud MCP server URL (must be a dbt Cloud
            host, e.g. https://cloud.getdbt.com/api/ai/v1/mcp/) and a service
            token from dbt Cloud. Tokens are stored encrypted and never returned
            to the browser.
          </Description>
        </FlexColumn>
        {editing === null && (
          <Button
            variant="text"
            color="secondary"
            disabled={!canEdit}
            onClick={() => setEditing('new')}
            startIcon={<PlusIcon />}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', width: 'fit-content' }}
          >
            Add connection
          </Button>
        )}
      </Flex>

      <FieldsContainer>
        {editing === 'new' ? (
          <ConnectionForm canEdit={canEdit} onDone={() => setEditing(null)} />
        ) : editingConnection ? (
          <ConnectionForm
            connection={editingConnection}
            canEdit={canEdit}
            onDone={() => setEditing(null)}
          />
        ) : (
          connections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              canEdit={canEdit}
              onEdit={() => setEditing(connection.id)}
            />
          ))
        )}
        {editing === null && connections.length === 0 && (
          <Typography variant="body2" color="content.translucent.secondary">
            No dbt Cloud connections yet.
          </Typography>
        )}
      </FieldsContainer>
    </Frame>
  );
};

export default AiMcpSection;
